import { Autocomplete, NumberInput, TextInput, Tooltip } from '@mantine/core';
import React, { useEffect, useState } from 'react';

import SaveState, { type SaveStatus } from '@components/ui/SaveState';
import { readApiError } from '@utils/apiError';

import styles from './InlineField.module.css';

export interface InlineFieldProps {
    label: string;
    /** The stored value, as the API returns it. Null or '' reads as empty. */
    value: string | null | undefined;
    /** Writes the new value; a blank string means "clear". Rejections show as the error. */
    onSave: (value: string) => Promise<void>;
    kind?: 'text' | 'number' | 'autocomplete';
    /** Decimal places for a number field; integers pass 0. */
    decimalScale?: number;
    /** The values to offer for an autocomplete; anything typed is still accepted. */
    options?: string[];
    placeholder?: string;
    hint?: string;
    /** Why the field cannot be edited, shown on hover. Read-only when set. */
    readOnlyReason?: string;
    /** Longer text gets a wider box. */
    mono?: boolean;
}

/**
 * Click to edit, blur or Enter saves, Escape abandons the draft.
 *
 * The same shape the vendor email field under Settings uses and the entity grid's cells
 * follow: a half-typed value saved by accident is worse than a lost keystroke, so Escape
 * reverts, and nothing is written when the draft equals what is stored. Each field reports
 * its own save state beside its label, with the server's reason when it fails.
 */
const InlineField: React.FC<InlineFieldProps> = ({
    label,
    value,
    onSave,
    kind = 'text',
    decimalScale,
    options,
    placeholder = 'Not set',
    hint,
    readOnlyReason,
    mono = false,
}) => {
    const stored = value == null ? '' : String(value);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(stored);
    const [status, setStatus] = useState<SaveStatus>('idle');
    const [error, setError] = useState<string | null>(null);

    // A reload after another field's save may bring a fresh stored value; follow it unless
    // the person is mid-edit on this one.
    useEffect(() => {
        if (!editing) setDraft(stored);
    }, [stored, editing]);

    const commit = async () => {
        const next = draft.trim();
        if (next === stored.trim()) {
            setEditing(false);
            return;
        }
        setStatus('saving');
        setError(null);
        try {
            await onSave(next);
            setStatus('saved');
            setEditing(false);
        } catch (err) {
            setError(readApiError(err, 'Could not save'));
            setStatus('failed');
        }
    };

    const abandon = () => {
        setDraft(stored);
        setEditing(false);
        setStatus('idle');
        setError(null);
    };

    const readView = (
        <div
            className={`${styles.readView} ${readOnlyReason ? styles.readOnly : ''} ${stored ? '' : styles.empty} ${mono ? 'mono' : ''}`}
            onClick={() => {
                if (readOnlyReason) return;
                setDraft(stored);
                setEditing(true);
            }}
            role={readOnlyReason ? undefined : 'button'}
            tabIndex={readOnlyReason ? -1 : 0}
            onKeyDown={(e) => {
                if (readOnlyReason) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDraft(stored);
                    setEditing(true);
                }
            }}
        >
            {stored || placeholder}
        </div>
    );

    return (
        <div className={styles.field}>
            <div className={styles.labelRow}>
                <span className={styles.label}>{label}</span>
                <SaveState status={status} message={error} />
            </div>
            {editing ? (
                kind === 'number' ? (
                    <NumberInput
                        size="xs"
                        autoFocus
                        value={draft === '' ? '' : Number(draft)}
                        onChange={(v) => setDraft(v === '' ? '' : String(v))}
                        decimalScale={decimalScale}
                        allowNegative={false}
                        disabled={status === 'saving'}
                        onBlur={() => void commit()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') void commit();
                            if (e.key === 'Escape') abandon();
                        }}
                    />
                ) : kind === 'autocomplete' ? (
                    <Autocomplete
                        size="xs"
                        autoFocus
                        data={options ?? []}
                        value={draft}
                        onChange={setDraft}
                        disabled={status === 'saving'}
                        onBlur={() => void commit()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') void commit();
                            if (e.key === 'Escape') abandon();
                        }}
                        comboboxProps={{ width: 'target', position: 'bottom-start' }}
                    />
                ) : (
                    <TextInput
                        size="xs"
                        autoFocus
                        value={draft}
                        onChange={(e) => setDraft(e.currentTarget.value)}
                        disabled={status === 'saving'}
                        onBlur={() => void commit()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') void commit();
                            if (e.key === 'Escape') abandon();
                        }}
                    />
                )
            ) : readOnlyReason ? (
                <Tooltip label={readOnlyReason} withArrow>
                    {readView}
                </Tooltip>
            ) : (
                readView
            )}
            {hint && <span className={styles.hint}>{hint}</span>}
        </div>
    );
};

export default InlineField;
